import { useEffect, useState } from 'react'
import { Button, Form, Input, InputNumber, Modal, Popconfirm, Space, Table, Tag, message } from 'antd'
import { purposePaymentApi } from '@/api'

const emptyForm = {
  name: '',
  amount: null,
  contract_number: '',
  payment_id: '',
}

const PurposePaymentsTab = ({ student }) => {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [editing, setEditing] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [form] = Form.useForm()

  const load = () => {
    if (!student?.id) return
    setLoading(true)
    purposePaymentApi
      .list(student.id)
      .then(({ data }) => setRows(data?.data ?? []))
      .catch(() => message.error('Не удалось загрузить оплаты по назначению'))
      .finally(() => setLoading(false))
  }

  useEffect(load, [student?.id])

  const openModal = (row = null) => {
    setEditing(row)
    setModalOpen(true)
    form.setFieldsValue(row ? {
      name: row.name,
      amount: row.amount,
      contract_number: row.contract_number,
      payment_id: row.payment_id,
    } : emptyForm)
  }

  const closeModal = () => {
    setEditing(null)
    setModalOpen(false)
    form.resetFields()
  }

  const save = async () => {
    const values = await form.validateFields()
    setSaving(true)
    const request = editing
      ? purposePaymentApi.update(editing.id, values)
      : purposePaymentApi.create({ ...values, user_id: student.id })
    request
      .then(() => {
        message.success(editing ? 'Назначение обновлено' : 'Назначение создано')
        closeModal()
        load()
      })
      .catch((error) => message.error(error?.response?.data?.error?.[0] ?? 'Не удалось сохранить назначение'))
      .finally(() => setSaving(false))
  }

  const columns = [
    { title: 'Название', dataIndex: 'name' },
    { title: 'Сумма', dataIndex: 'amount', render: (value) => `${value} ₽` },
    { title: 'Номер договора', dataIndex: 'contract_number', render: (value) => value || '—' },
    {
      title: 'ID платежа',
      dataIndex: 'payment_id',
      render: (value) => (
        <a href={`${window.location.origin}/purpose-payment/${encodeURIComponent(value)}`} target="_blank" rel="noreferrer">
          {value}
        </a>
      ),
    },
    {
      title: 'Статус',
      dataIndex: 'status_name',
      render: (value, row) => <Tag color={row.status === 1 ? 'green' : 'orange'}>{value}</Tag>,
    },
    {
      title: 'Действия',
      render: (_, row) => (
        <Space wrap>
          {row.status !== 1 && <Button size="small" onClick={() => openModal(row)}>Изменить</Button>}
          {row.status !== 1 && (
            <Popconfirm title="Отметить назначение оплаченным?" onConfirm={() => purposePaymentApi.markPaid(row.id).then(load)}>
              <Button size="small" type="primary">Оплачено</Button>
            </Popconfirm>
          )}
          <Popconfirm title="Удалить назначение из карточки и финансовых отчётов?" onConfirm={() => purposePaymentApi.remove(row.id).then(load)}>
            <Button size="small" danger>Удалить</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <div>
      <Button type="primary" onClick={() => openModal()}>Добавить назначение</Button>
      <Table
        style={{ marginTop: 16 }}
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={false}
        scroll={{ x: 900 }}
      />
      <Modal
        open={modalOpen}
        title={editing ? 'Изменить назначение' : 'Новое назначение'}
        onCancel={closeModal}
        onOk={save}
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Название" rules={[{ required: true, message: 'Введите название' }]}>
            <Input maxLength={255} />
          </Form.Item>
          <Form.Item name="amount" label="Сумма" rules={[{ required: true, message: 'Введите сумму' }]}>
            <InputNumber min={0.01} precision={2} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="contract_number" label="Номер договора (необязательно)">
            <Input maxLength={255} />
          </Form.Item>
          <Form.Item name="payment_id" label="Уникальный ID платежа" extra="Оставьте пустым — ID сформируется автоматически.">
            <Input maxLength={64} disabled={Boolean(editing?.status === 1)} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default PurposePaymentsTab
